import React from 'react';
import { Helmet } from 'react-helmet';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaMusic, FaFlask, FaTheaterMasks, FaRunning, FaCamera, FaMicrophone, FaStar } from 'react-icons/fa';
import SocialShare from '../components/SocialShare';

const Events = () => {
  const upcomingEvents = [
    {
      id: 1,
      title: 'Annual Science Fair',
      date: 'March 15, 2024',
      time: '9:00 AM - 3:00 PM',
      location: 'School Auditorium',
      description: 'Students showcase their innovative projects and scientific discoveries. Open to parents and community members.',
      icon: FaFlask,
      category: 'Academic',
      color: 'blue',
      featured: true
    },
    {
      id: 2,
      title: 'Spring Drama Festival',
      date: 'April 20, 2024',
      time: '7:00 PM - 9:00 PM',
      location: 'School Theater',
      description: 'A celebration of performing arts featuring student productions of classic and contemporary plays.',
      icon: FaTheaterMasks,
      category: 'Arts',
      color: 'purple',
      featured: true
    },
    {
      id: 3,
      title: 'Sports Day',
      date: 'May 10, 2024',
      time: '8:00 AM - 4:00 PM',
      location: 'School Grounds',
      description: 'Annual athletic competition promoting fitness and teamwork. Students compete in various sports events.',
      icon: FaRunning,
      category: 'Sports',
      color: 'green',
      featured: false
    },
    {
      id: 4,
      title: 'Music Concert',
      date: 'May 25, 2024',
      time: '6:00 PM - 8:00 PM',
      location: 'School Auditorium',
      description: 'Annual music concert featuring performances by school bands, choirs, and solo artists.',
      icon: FaMusic,
      category: 'Arts',
      color: 'pink',
      featured: false
    },
    {
      id: 5,
      title: 'Photography Exhibition',
      date: 'June 5, 2024',
      time: '10:00 AM - 5:00 PM',
      location: 'Art Gallery',
      description: 'Student photography exhibition showcasing creative works from photography classes.',
      icon: FaCamera,
      category: 'Arts',
      color: 'yellow',
      featured: false
    },
    {
      id: 6,
      title: 'Debate Competition',
      date: 'June 15, 2024',
      time: '2:00 PM - 5:00 PM',
      location: 'Conference Room',
      description: 'Inter-school debate competition focusing on current affairs and social issues.',
      icon: FaMicrophone,
      category: 'Academic',
      color: 'red',
      featured: false
    }
  ];

  const academicCalendar = [
    {
      month: 'January',
      events: [
        { date: '8', type: 'term', title: 'Spring Term Begins', icon: '📚' },
        { date: '15', type: 'holiday', title: 'Martin Luther King Day', icon: '🎉' },
        { date: '25', type: 'exam', title: 'Mid-term Exams', icon: '📝' }
      ]
    },
    {
      month: 'February',
      events: [
        { date: '12', type: 'holiday', title: 'Presidents Day', icon: '🎉' },
        { date: '20', type: 'event', title: 'Parent-Teacher Conference', icon: '👥' }
      ]
    },
    {
      month: 'March',
      events: [
        { date: '15', type: 'event', title: 'Science Fair', icon: '🔬' },
        { date: '25', type: 'exam', title: 'Quarter Exams', icon: '📝' }
      ]
    },
    {
      month: 'April',
      events: [
        { date: '1-5', type: 'holiday', title: 'Spring Break', icon: '🌷' },
        { date: '20', type: 'event', title: 'Drama Festival', icon: '🎭' }
      ]
    },
    {
      month: 'May',
      events: [
        { date: '10', type: 'event', title: 'Sports Day', icon: '🏃' },
        { date: '25', type: 'event', title: 'Music Concert', icon: '🎵' },
        { date: '30', type: 'exam', title: 'Final Exams', icon: '📝' }
      ]
    },
    {
      month: 'June',
      events: [
        { date: '5', type: 'event', title: 'Photography Exhibition', icon: '📸' },
        { date: '15', type: 'event', title: 'Debate Competition', icon: '🎤' },
        { date: '20', type: 'graduation', title: 'Graduation Ceremony', icon: '🎓' }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-600 text-blue-600',
      purple: 'bg-purple-600 text-purple-600',
      green: 'bg-green-600 text-green-600',
      pink: 'bg-pink-600 text-pink-600',
      yellow: 'bg-yellow-600 text-yellow-600',
      red: 'bg-red-600 text-red-600'
    };
    return colors[color] || colors.blue;
  };

  const getEventTypeClasses = (type) => {
    const types = {
      term: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      holiday: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      exam: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      event: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      graduation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    };
    return types[type] || types.event;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Helmet>
        <title>Rasoha Academy | Events & Academic Calendar</title>
        <meta name="description" content="Stay updated with Rasoha Academy's upcoming events, academic calendar, and school activities. Discover our vibrant school community and educational programs." />
        <meta name="keywords" content="Rasoha Academy Events, School Calendar, Academic Events, Narok School Activities, Student Events, School Programs" />
        <meta property="og:title" content="Rasoha Academy | Events & Academic Calendar" />
        <meta property="og:description" content="Stay updated with Rasoha Academy's upcoming events, academic calendar, and school activities." />
        <meta property="og:url" content="https://rasohaacademy.com/events" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            School Events
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90 mb-6">
            Stay updated with our latest events, activities, and important dates
          </p>
          <div className="flex justify-center">
            <SocialShare 
              title="Rasoha Academy Events"
              description="Stay updated with our latest school events and activities"
              url="https://rasohaacademy.com/events"
            />
          </div>
        </div>
      </section>

      {/* Upcoming Events Timeline */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Upcoming Events
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Mark your calendars for these exciting school events
            </p>
          </div>

          <div className="space-y-8">
            {upcomingEvents.map((event, index) => (
              <div
                key={event.id}
                className={`relative ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex flex-col lg:flex-row items-center gap-8 p-6 bg-white dark:bg-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
                data-aos={index % 2 === 0 ? 'fade-right' : 'fade-left'}
                data-aos-delay={index * 100}
              >
                {/* Event Icon */}
                <div className={`w-20 h-20 ${getColorClasses(event.color).split(' ')[0]} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <event.icon className="w-10 h-10 text-white" />
                </div>

                {/* Event Details */}
                <div className="flex-1 text-center lg:text-left">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-0">
                      {event.title}
                      {event.featured && (
                        <span className="ml-2 inline-block">
                          <FaStar className="w-5 h-5 text-yellow-500" />
                        </span>
                      )}
                    </h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getColorClasses(event.color).split(' ')[1]} bg-opacity-10`}>
                      {event.category}
                    </span>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 text-gray-600 dark:text-gray-300">
                    <div className="flex items-center">
                      <FaCalendarAlt className="w-4 h-4 mr-2" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center">
                      <FaClock className="w-4 h-4 mr-2" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="w-4 h-4 mr-2" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {event.description}
                  </p>
                </div>

                {/* CTA Button */}
                <div className="flex-shrink-0">
                  <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Calendar */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Academic Calendar 2024
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Key dates and important events throughout the academic year
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {academicCalendar.map((month, index) => (
              <div
                key={month.month}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center border-b border-gray-200 dark:border-gray-700 pb-2">
                  {month.month}
                </h3>
                <div className="space-y-3">
                  {month.events.map((event, eventIndex) => (
                    <div
                      key={eventIndex}
                      className={`p-3 rounded-lg ${getEventTypeClasses(event.type)}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">{event.icon}</span>
                          <span className="font-medium">{event.title}</span>
                        </div>
                        <span className="text-sm font-bold">{event.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Stay Connected
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Don't miss out on any school events. Subscribe to our newsletter for updates.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              Subscribe to Updates
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105">
              View Full Calendar
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events; 